import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, FileText, Loader2, Send, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { http, API } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Seo } from "@/components/Seo";

function UnlockGuideDialog({ guide, shared, onClose }) {
  const [email, setEmail] = useState(localStorage.getItem("bh_lead_email") || "");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);

  const unlock = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await http.post("/guides/download", {
        email,
        name: name || null,
        guide_slug: guide.slug,
        source: shared ? "guide_share" : "guides_page",
      });
      localStorage.setItem("bh_lead_email", email);
      toast.success("Guide débloqué : bonne lecture !");
      window.open(`${API}/guides/${guide.slug}/fr.pdf`, "_blank", "noopener");
      onClose();
    } catch {
      toast.error("Impossible de débloquer le guide. Vérifiez votre email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="unlock-guide-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-slate-900 flex items-center gap-2">
            <Sparkles size={18} className="text-brand-600" /> {shared ? "Un guide vous a été recommandé" : "Recevoir ce guide gratuitement"}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            « {guide.title} » — entrez votre email pour débloquer le guide et le recevoir gratuitement.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={unlock} className="space-y-4 mt-2">
          <div>
            <label htmlFor="unlock-email" className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              Votre email *
            </label>
            <input
              id="unlock-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@email.fr"
              data-testid="unlock-email-input"
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label htmlFor="unlock-name" className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              Votre prénom (facultatif)
            </label>
            <input
              id="unlock-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Marie"
              data-testid="unlock-name-input"
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <button type="submit" disabled={sending} data-testid="unlock-submit-btn" className="btn-primary text-sm w-full">
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} strokeWidth={2} />} Débloquer mon guide
          </button>
          <p className="text-[11px] text-slate-400 text-center">Aucun spam. Désinscription en 1 clic. Vous recevrez également le guide par email.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ShareGuideDialog({ guide }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [sending, setSending] = useState(false);

  const share = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await http.post("/guides/share", {
        guide_slug: guide.slug,
        recipient_email: email,
        sender_name: senderName || null,
      });
      toast.success("Guide envoyé");
      setOpen(false);
      setEmail("");
      setSenderName("");
    } catch {
      toast.error("Envoi impossible. Vérifiez l'adresse email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={`guide-share-${guide.slug}`}
          className="btn-outline text-sm w-full"
        >
          <Share2 size={15} strokeWidth={2} /> Partager
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="share-guide-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-slate-900">Partager ce guide</DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Le destinataire recevra « {guide.title} » par email, gratuitement.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={share} className="space-y-4 mt-2">
          <div>
            <label htmlFor={`share-email-${guide.slug}`} className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              Email du destinataire *
            </label>
            <input
              id={`share-email-${guide.slug}`}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="destinataire@email.fr"
              data-testid="share-email-input"
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div>
            <label htmlFor={`share-name-${guide.slug}`} className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              Votre prénom (facultatif)
            </label>
            <input
              id={`share-name-${guide.slug}`}
              type="text"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="Marie"
              data-testid="share-name-input"
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <button type="submit" disabled={sending} data-testid="share-submit-btn" className="btn-primary text-sm w-full">
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} strokeWidth={2} />} Envoyer le guide
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function GuidesPage() {
  const [guides, setGuides] = useState([]);
  const [unlockGuide, setUnlockGuide] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    http.get("/guides")
      .then((r) => setGuides(r.data))
      .catch(() => toast.error("Impossible de charger les guides. Veuillez actualiser la page."));
  }, []);

  const sharedSlug = searchParams.get("partage");
  const sharedGuide = sharedSlug ? guides.find((g) => g.slug === sharedSlug) : null;
  const closeUnlock = () => {
    setUnlockGuide(null);
    if (sharedSlug) {
      searchParams.delete("partage");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const requestGuide = async (g) => {
    const saved = localStorage.getItem("bh_lead_email");
    if (saved) {
      try {
        await http.post("/guides/download", { email: saved, guide_slug: g.slug, source: "guides_page" });
        toast.success("Guide débloqué : bonne lecture !");
        window.open(`${API}/guides/${g.slug}/fr.pdf`, "_blank", "noopener");
        return;
      } catch {
        // Email enregistré invalide : on redemande via le dialog.
      }
    }
    setUnlockGuide(g);
  };

  return (
    <div data-testid="guides-page">
      <Seo title="Guides gratuits sur le développement de l'enfant" description="Guides de référence du Center on the Developing Child (Harvard) traduits en français : développement cérébral, stress toxique, servir et retourner. Téléchargement gratuit." />
      {sharedGuide && !unlockGuide && <UnlockGuideDialog guide={sharedGuide} shared onClose={closeUnlock} />}
      {unlockGuide && <UnlockGuideDialog guide={unlockGuide} onClose={closeUnlock} />}
      <section className="container-x pt-16 pb-6">
        <div className="max-w-3xl">
          <div className="eyebrow mb-3">Ressources gratuites · Développement de l'enfant</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1]">
            Les guides sur le développement de l'enfant.
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Recevez gratuitement des guides de référence sur le développement de l'enfant, issus de centres
            de recherche de référence mondiaux : Harvard Center on the Developing Child, OMS, UNICEF.
            Entrez simplement votre email pour les débloquer. Aucun spam, désinscription en 1 clic.
          </p>
        </div>
      </section>

      <section className="container-x py-10">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {guides.map((g) => (
            <article key={g.slug} className="card-soft p-7 flex flex-col" data-testid={`guide-card-${g.slug}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="grid place-items-center h-11 w-11 rounded-2xl bg-brand-100 text-brand-700">
                  <FileText strokeWidth={1.5} size={20} />
                </div>
                <span className="text-xs font-semibold uppercase tracking-widest text-brand-700 bg-brand-50 rounded-full px-3 py-1">{g.tag}</span>
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900 leading-snug">{g.title}</h3>
              <p className="text-xs text-slate-400 mt-1">{g.source_org}</p>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed flex-1">{g.description}</p>
              <div className="mt-6 space-y-2">
                <button
                  onClick={() => requestGuide(g)}
                  data-testid={`guide-download-${g.slug}`}
                  className="btn-primary text-sm w-full"
                >
                  <Download size={16} strokeWidth={2} /> Recevoir le guide gratuit
                </button>
                <ShareGuideDialog guide={g} />
                <a
                  href={g.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`guide-original-${g.slug}`}
                  className="block text-center text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2"
                >
                  Source officielle
                </a>
              </div>
            </article>
          ))}
        </div>
        <p className="mt-10 text-xs text-slate-500 max-w-2xl">
          Traductions, synthèses et adaptations françaises par Beehive Academy, d'après les ressources du
          Center on the Developing Child — Harvard University.
        </p>
      </section>
    </div>
  );
}
